import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import TournamentList from './pages/TournamentList';
import TournamentDetail from './pages/TournamentDetail';
import CreateTournament from './pages/CreateTournament';
import Profile from './pages/Profile';
import ClubManagement from './pages/ClubManagement';
import PublicTournament from './pages/PublicTournament';
import PublicRegistration from './pages/PublicRegistration';
import PlayerProfile from './pages/PlayerProfile';
import PlayersDirectory from './pages/PlayersDirectory';
import SportsPage from './pages/SportsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import InviteAccept from './pages/InviteAccept';
import LiveScoringPage from './pages/LiveScoringPage';
import AdminPanel from './pages/AdminPanel';
import ForgotPassword from './pages/ForgotPassword';
import Footer from './components/Footer';
import SyncManager from './pages/desktop/SyncManager';
import LicenseBanner from './components/desktop/LicenseBanner';
import PlayerDashboard from './pages/player/PlayerDashboard';
import MyTournaments from './pages/player/MyTournaments';
import MyPlayerProfile from './pages/player/MyPlayerProfile';
import { Toaster } from 'react-hot-toast';
import { ConfirmProvider } from './components/ConfirmDialog';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const OrganizerRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!user || !['admin', 'organizer'].includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const PlayerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!user || user.role !== 'player') return <Navigate to="/" />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Admin panel is a SaaS-only feature — not available in the desktop app
  if (typeof window !== 'undefined' && window.electronAPI) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
};

// In the desktop app with an expired license, only /profile and /sync are accessible.
// All other authenticated routes redirect to /profile so the user can renew.
const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
const LicenseExpiredRoute = ({ children }) => {
  const { licenseExpired, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (isElectron && licenseExpired) return <Navigate to="/profile" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
      <Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { fontSize: '0.875rem', maxWidth: 360 },
            success: { iconTheme: { primary: '#0D9488', secondary: '#fff' } },
          }}
        />
        <div className="app">
          <LicenseBanner />
          <Header />
          <main className="main">
            <Routes>
              <Route path="/" element={<LicenseExpiredRoute><Home /></LicenseExpiredRoute>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/tournaments" element={<LicenseExpiredRoute><TournamentList /></LicenseExpiredRoute>} />
              <Route path="/tournaments/:id" element={<LicenseExpiredRoute><TournamentDetail /></LicenseExpiredRoute>} />
              <Route
                path="/create-tournament"
                element={
                  <LicenseExpiredRoute>
                    <OrganizerRoute>
                      <CreateTournament />
                    </OrganizerRoute>
                  </LicenseExpiredRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clubs"
                element={
                  <LicenseExpiredRoute>
                    <OrganizerRoute>
                      <ClubManagement />
                    </OrganizerRoute>
                  </LicenseExpiredRoute>
                }
              />
              <Route path="/t/:id" element={<PublicTournament />} />
              <Route path="/t/:id/register" element={<PublicRegistration />} />
              <Route path="/players" element={<PlayersDirectory />} />
              <Route path="/sports" element={<SportsPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/profiles/:profileId" element={<PlayerProfile />} />
              <Route path="/invites/accept/:token" element={<InviteAccept />} />
              <Route path="/live-score/:matchId" element={<LicenseExpiredRoute><PrivateRoute><LiveScoringPage /></PrivateRoute></LicenseExpiredRoute>} />
              <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
              <Route path="/sync" element={<PrivateRoute><SyncManager /></PrivateRoute>} />
              <Route path="/player/dashboard" element={<PlayerRoute><PlayerDashboard /></PlayerRoute>} />
              <Route path="/player/tournaments" element={<PlayerRoute><MyTournaments /></PlayerRoute>} />
              <Route path="/player/profile" element={<PlayerRoute><MyPlayerProfile /></PlayerRoute>} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
